import {
  TextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import lodash from "lodash";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import { Entity, Mesh } from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./EntityConfig.module.css";

interface Props {
  entity: Entity;
  panelId: string;
}

export function MeshConfig({ entity, panelId }: Props) {
  const dispatch = useVizDispatch();

  // Mesh settings
  const [meshType, setMeshType] = React.useState<Mesh["type"]>(
    entity.mesh?.type ?? "sphere",
  );
  const [radius, setRadius] = React.useState(
    `${entity.mesh?.type === "sphere" ? entity.mesh.radius : 0.25}`,
  );
  const [radiusError, setRadiusError] = React.useState("");
  const [size, setSize] = React.useState(
    `${entity.mesh?.type === "box" ? entity.mesh.size : 0.5}`,
  );
  const [sizeError, setSizeError] = React.useState("");
  const [color, setColor] = React.useState(entity.mesh?.color || "#4b77b4");

  React.useEffect(
    function onMeshFieldsChanged() {
      let updatedMesh: Mesh;
      if (meshType === "sphere") {
        setRadiusError("");
        const parsedRadius = Number.parseFloat(radius);
        if (!Number.isFinite(parsedRadius)) {
          setRadiusError("Radius must be a number");
          return;
        } else if (parsedRadius <= 0) {
          setRadiusError("Radius must be positive");
          return;
        }
        updatedMesh = {
          type: "sphere",
          color,
          radius: parsedRadius,
        };
      } /* if (meshType === "box") */ else {
        setSizeError("");
        const parsedSize = Number.parseFloat(size);
        if (!Number.isFinite(parsedSize)) {
          setSizeError("Size must be a number");
          return;
        } else if (parsedSize <= 0) {
          setSizeError("Size must be positive");
          return;
        }
        updatedMesh = {
          type: "box",
          color,
          size: parsedSize,
        };
      }
      if (!lodash.isEqual(updatedMesh, entity.mesh)) {
        dispatch(updateThreeDEntity(panelId, entity.id, { mesh: updatedMesh }));
      }
    },
    [
      meshType,
      color,
      radius,
      size,
      entity,
      dispatch,
      panelId,
      setRadiusError,
      setSizeError,
    ],
  );

  return (
    <>
      <RobotoTypography variant="body2" className={styles.fieldLabel}>
        Mesh
      </RobotoTypography>
      <div className={styles.container}>
        {/*<ConfigItem label="Mesh Type" />*/}
        <FormControl size="small" fullWidth className={styles.field}>
          <InputLabel id="mesh-type-label" className={styles.input}>
            Mesh Type
          </InputLabel>
          <Select
            label={"Mesh Type"}
            labelId={"mesh-type-label"}
            value={meshType}
            onChange={(e) => setMeshType(e.target.value as "sphere" | "box")}
            className={styles.input}
            inputProps={{ className: styles.input }}
          >
            <MenuItem value="sphere">Sphere</MenuItem>
            <MenuItem value="box">Box</MenuItem>
          </Select>
        </FormControl>
        {meshType === "sphere" && (
          <div className={styles.fieldRow}>
            <TextField
              label="Radius"
              autoComplete="off"
              type="number"
              size="small"
              margin="dense"
              fullWidth
              value={radius}
              error={!!radiusError}
              helperText={radiusError}
              onChange={(e) => setRadius(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 0.1, step: 0.1, className: styles.input }}
              className={styles.input}
            />
            <TextField
              label="Color"
              type="color"
              size="small"
              margin="dense"
              fullWidth
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className={styles.input}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ className: styles.input }}
            />
          </div>
        )}

        {meshType === "box" && (
          <div className={styles.fieldRow}>
            <TextField
              label="Length"
              autoComplete="off"
              type="number"
              size="small"
              margin="dense"
              fullWidth
              value={size}
              error={!!sizeError}
              helperText={sizeError}
              onChange={(e) => setSize(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 0.1, step: 0.1, className: styles.input }}
              className={styles.input}
            />
            <TextField
              label="Color"
              type="color"
              size="small"
              margin="dense"
              fullWidth
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className={styles.input}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ className: styles.input }}
            />
          </div>
        )}
      </div>
    </>
  );
}
